/**
 * Resolve Base / Slotted labels for Explore Unknown level-tree erray slots.
 * Prefers game config (`effects`, `refinedErrayEffect`); falls back to i18n.
 */

function trimPercentLabel(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  return s || null;
}

/** @param {number} value 0–1 fraction or whole percent (e.g. 5 for 5%) */
export function formatExploreUnknownPercentLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n > 0 && n <= 1) {
    const pct = n * 100;
    if (Math.abs(pct - Math.round(pct)) < 1e-6) {
      return `${Math.round(pct)}%`;
    }
    const rounded = Math.round(pct * 10) / 10;
    return `${rounded}%`;
  }
  if (Number.isInteger(n)) {
    return `${n}%`;
  }
  return `${n}%`;
}

function parsePercentNumber(label) {
  if (!label) return null;
  const m = String(label).match(/(\d+(?:\.\d+)?)\s*%/);
  return m ? Number(m[1]) : null;
}

function combinePercentLabels(baseLabel, slottedHintLabel) {
  const baseN = parsePercentNumber(baseLabel);
  const slotN = parsePercentNumber(slottedHintLabel);
  if (baseN == null || slotN == null) {
    return slottedHintLabel || baseLabel;
  }
  if (slotN >= baseN) {
    return slottedHintLabel;
  }
  return formatExploreUnknownPercentLabel(baseN + slotN);
}

function chargeLabel(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 1) return null;
  return n === 1 ? '1 charge' : `${n} charges`;
}

/**
 * @param {object} def Level definition from exploreUnknownLeveling.json
 * @param {(key: string, opts?: object) => string} t i18next `t`
 * @param {string} i18nKey e.g. levelBonus23
 * @returns {{ baseLabel: string, slottedLabel: string } | null}
 */
export function resolveExploreUnknownErraySlotLabels(def, t, i18nKey) {
  if (!def?.refinedErraySlot) return null;

  const prefix = `exploreUnknownPage.${i18nKey}`;
  const i18nBase = trimPercentLabel(t(`${prefix}.baseBonus`, { defaultValue: '' }));
  const i18nSlotted = trimPercentLabel(t(`${prefix}.slottedBonus`, { defaultValue: '' }));

  const fx = def.effects || {};
  const slotFx = def.refinedErrayEffect || {};
  let derivedBase = null;
  let derivedSlotted = null;

  if (fx.kind === 'mapProc' && fx.chance != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.chance);
    if (slotFx.chance != null) {
      derivedSlotted = formatExploreUnknownPercentLabel(slotFx.chance);
    }
  } else if (fx.chance != null && typeof fx.chance === 'number') {
    derivedBase = formatExploreUnknownPercentLabel(fx.chance);
    if (slotFx.chance != null) {
      derivedSlotted = formatExploreUnknownPercentLabel(slotFx.chance);
    }
  } else if (fx.kind === 'spawnWeightDelta' && fx.delta != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.delta);
  } else if (fx.kind === 'spawnWeightConditional' && fx.weight != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.weight);
  } else if (fx.kind === 'darknessReductionPerWin' && fx.percent != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.percent);
  } else if (fx.kind === 'memoryVariantBonus' && fx.darknessBonus != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.darknessBonus);
  } else if (fx.kind === 'unlockMemoryVariant' && fx.spawnWeight != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.spawnWeight);
  } else if (fx.kind === 'nextMissionXpMultiplier' && fx.multiplier != null) {
    const extra = Math.round((Number(fx.multiplier) - 1) * 100);
    if (Number.isFinite(extra) && extra > 0) {
      derivedBase = formatExploreUnknownPercentLabel(extra);
    }
  } else if (fx.kind === 'skipWorldOption' && fx.skipTwoWorldsChance != null) {
    derivedBase = formatExploreUnknownPercentLabel(fx.skipTwoWorldsChance);
  } else if (fx.kind === 'preparationBonus') {
    const uses =
      fx.usesPerBattle != null && Number.isFinite(Number(fx.usesPerBattle))
        ? Number(fx.usesPerBattle)
        : 1;
    derivedBase = chargeLabel(uses);
    const add = Number(slotFx.usesPerBattleBonus);
    if (Number.isFinite(add) && add > 0) {
      derivedSlotted = chargeLabel(uses + add);
    }
  } else if (fx.kind === 'excessHealthConversion' && fx.healthGain != null) {
    const n = Number(fx.healthGain);
    derivedBase = n === 1 ? '1 health' : `${n} health`;
  }

  const baseLabel = i18nBase || derivedBase;
  let slottedLabel =
    (slotFx && Object.keys(slotFx).length > 0 ? derivedSlotted : null) ||
    i18nSlotted ||
    derivedSlotted;

  if (baseLabel && slottedLabel && /%/.test(baseLabel) && /%/.test(slottedLabel)) {
    slottedLabel = combinePercentLabels(baseLabel, slottedLabel);
  } else if (!slottedLabel && baseLabel) {
    slottedLabel = baseLabel;
  }

  if (!baseLabel || !slottedLabel) {
    return null;
  }
  if (baseLabel === '—' || baseLabel === '-' || slottedLabel === '—' || slottedLabel === '-') {
    return null;
  }

  return { baseLabel, slottedLabel };
}

/**
 * @param {string} title
 * @param {string} effectiveLabel percent or charge label currently active
 */
export function replaceFirstMetricInTitle(title, effectiveLabel) {
  if (!title || !effectiveLabel) {
    return { before: title || '', highlight: null, after: '' };
  }

  const percentMatch = title.match(/(\d+(?:\.\d+)?)\s*%/);
  if (percentMatch && /%/.test(effectiveLabel)) {
    const idx = percentMatch.index;
    return {
      before: title.slice(0, idx),
      highlight: effectiveLabel,
      after: title.slice(idx + percentMatch[0].length),
    };
  }

  const chargeMatch = title.match(/\d+\s+charge(s)?/i);
  if (chargeMatch && /charge/i.test(effectiveLabel)) {
    const idx = chargeMatch.index;
    return {
      before: title.slice(0, idx),
      highlight: effectiveLabel,
      after: title.slice(idx + chargeMatch[0].length),
    };
  }

  return { before: title, highlight: null, after: '' };
}
